/**
 * One shape for "a client action failed", and the two ways to build it.
 *
 * Before this existed, twelve modules each re-declared `{ ok: false; error:
 * string }` inline and each invented its own failure text — `no token`,
 * `fetch failed`, `empty response from server`, `request failed (500)`. The
 * UI had nothing else to branch on, so those strings were what users read.
 * `update failed (500)` is the same construction as the
 * `LLM Gateway call failed (402): credit_floor` this work started from; the
 * anti-pattern grew independently on both sides of the system, which is why
 * the fix is a shared shape rather than another round of call-site edits.
 *
 * The contract: `error` is a diagnostic for logs and debugging and is never
 * rendered. Callers branch on `code` (and `status` when that is all the
 * backend gave) and show their own localized copy. `details` carries the
 * backend's structured context for `code` — the cap behind
 * `site_limit_reached`, for instance.
 *
 * Enforced by `tests/unit/user-facing-error-copy.test.ts`.
 */
import { ApiFetchError } from '@/hooks/api-fetcher'

export type ActionError = {
  ok: false
  /** Diagnostic text. Never render this — see the module docstring. */
  error: string
  /** Backend classification code, when the response carried one. */
  code?: string
  /** HTTP status, when the failure came from a response. */
  status?: number
  /** Backend's structured context for `code`. */
  details?: Record<string, unknown>
}

export type ActionResult<T> = { ok: true; data: T } | ActionError

/** The API's error envelope: `{success, data, error: {code, message, details}}`. */
interface ApiErrorEnvelope {
  error?: {
    code?: string
    message?: string
    details?: Record<string, unknown>
  } | null
}

/**
 * `ApiFetchError.details` holds the whole `{code, message, details}` envelope,
 * so the backend's own `details` sits one level in. Return that inner object
 * when it is there, and the envelope itself for the flatter shapes
 * (`body.detail`, top-level error bodies) the fetcher also produces.
 */
function innerDetails(envelope: unknown): Record<string, unknown> | undefined {
  if (typeof envelope !== 'object' || envelope === null) return undefined
  const inner = (envelope as Record<string, unknown>).details
  if (typeof inner === 'object' && inner !== null) {
    return inner as Record<string, unknown>
  }
  return envelope as Record<string, unknown>
}

/**
 * Build a failure from a non-OK `Response`, reading the API error envelope
 * for the code and details. Consumes the body, so call it once per response.
 */
export async function actionErrorFromResponse(
  resp: Response,
): Promise<ActionError> {
  let body: ApiErrorEnvelope | undefined
  try {
    body = (await resp.json()) as ApiErrorEnvelope
  } catch {
    // A non-JSON body (HTML error page, empty 204) leaves only the status.
  }
  return {
    ok: false,
    error: body?.error?.message || `request failed (${resp.status})`,
    ...(body?.error?.code ? { code: body.error.code } : {}),
    status: resp.status,
    ...(body?.error?.details ? { details: body.error.details } : {}),
  }
}

/**
 * Build a failure from a thrown value, keeping the classification an
 * `ApiFetchError` carries. Everything else becomes a bare diagnostic.
 */
export function actionErrorFromThrown(err: unknown): ActionError {
  if (err instanceof ApiFetchError) {
    const details = innerDetails(err.details)
    return {
      ok: false,
      error: err.message,
      ...(err.code ? { code: err.code } : {}),
      status: err.statusCode,
      ...(details ? { details } : {}),
    }
  }
  return {
    ok: false,
    error: err instanceof Error ? err.message : 'request threw a non-Error',
  }
}

/**
 * A failure the client itself decided on, with no response behind it — a
 * missing token, an absent brand id. `code` is what the UI branches on.
 */
export function localActionError(code: string, error: string): ActionError {
  return { ok: false, error, code }
}
